import { useCallback, useEffect, useMemo, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { isNil, omit, omitBy, pick } from "lodash";

import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import { useFormWithNotification } from "@covertree/hooks";
import {
  ExtraCoveragesForm,
  extraCoveragesFormValidation,
} from "@covertree/new-business-forms";
import {
  extraCoverageKeys,
  CoverageMineSubsidenceScheduledItemInput as OtherStructure,
  PersonalProperty,
  policyLevelExtraCoverageKeys,
  SavePolicyLevelExtraCoveragesInput,
  SaveUnitLevelExtraCoveragesInput,
  UnitLevelExtraCoveragesInput,
  useGetExtraCoveragesPricesQuery,
} from "@covertree/new-business-graphql";
import {
  selectChosenExtraCoverages,
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Form } from "@covertree/ui";

import { ExtraCoveragesView } from "./ExtraCoveragesView";
import {
  compareExtraCoveragesOnPage,
  extraCoverageKeyToAccessorMap,
} from "@covertree/new-business-mappings";

interface ExtraCoveragesProps {
  isD2C: boolean;
  handleExtraCoveragesSubmit(values: {
    policyLevelExtraCoverages: SavePolicyLevelExtraCoveragesInput;
    unitLevelExtraCoverages: SaveUnitLevelExtraCoveragesInput[];
  }): Promise<void>;
  onHandleExtraCoveragesSubmitError(formValues: ExtraCoveragesForm): void;
  onHandleExtraCoveragesSubmitStarted(): void;
  onLoading(): void;
  onLoadingError(): void;
  onLoadingFinished(): void;
}

export function ExtraCoverages({
  isD2C,
  handleExtraCoveragesSubmit,
  onHandleExtraCoveragesSubmitError,
  onHandleExtraCoveragesSubmitStarted,
  onLoadingFinished,
  onLoadingError,
  onLoading,
}: ExtraCoveragesProps) {
  const location = useLocation();

  const policy = useNewBusinessSelector(selectPolicy)!;
  const chosenExtraCoverages = useNewBusinessSelector(
    selectChosenExtraCoverages
  );

  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);

  const chosenExtraCoveragesMap = useMemo(
    () => Object.fromEntries(chosenExtraCoverages),
    [chosenExtraCoverages]
  );

  const defaultValues = useMemo(() => {
    if (location.state?.stepFormState) return location.state.stepFormState;

    return Object.fromEntries(
      chosenExtraCoverages.map(([k, { value }]) => [k, value])
    );
  }, [chosenExtraCoverages, location]);

  const { data, loading, error } = useGetExtraCoveragesPricesQuery({
    variables: {
      policyLocator: policy.policyLocator,
    },
  });

  useEffect(() => {
    if (loading) {
      onLoading();
    }

    if (!loading && data) {
      onLoadingFinished();
    }

    if (!loading && error) {
      onLoadingError();
    }
  }, [data, loading, error, onLoading, onLoadingError, onLoadingFinished]);

  const form = useFormWithNotification<ExtraCoveragesForm>({
    mode: "all",
    defaultValues,
    delayError: FORM_ERROR_DELAY_IN_MS,
    resolver: yupResolver(extraCoveragesFormValidation),
  });

  // TODO Types of extraCoverage from mapping should include valueIsCustomizable but it doesn't, don't know why
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const extraCoveragesToBeRendered: ExtraCoverageCardProps[] = useMemo(
    () =>
      data?.getExtraCoveragePrices
        ? extraCoverageKeys
            .filter(
              (extraCoverageKey) => !!chosenExtraCoveragesMap[extraCoverageKey]
            )
            .map((extraCoverageKey) => ({
              extraCoverage: chosenExtraCoveragesMap[extraCoverageKey],
              extraCoverageAccessor:
                extraCoverageKeyToAccessorMap[extraCoverageKey],
              extraCoverageKey,
              yearlyPrice:
                extraCoverageKey === "scheduledPersonalProperty" ||
                extraCoverageKey === "identityFraud" ||
                extraCoverageKey === "mineSubsidenceScheduled"
                  ? 0
                  : data!.getExtraCoveragePrices!.prices[extraCoverageKey] ?? 0,
              monthlyPrice:
                extraCoverageKey === "scheduledPersonalProperty" ||
                extraCoverageKey === "identityFraud" ||
                extraCoverageKey === "mineSubsidenceScheduled"
                  ? 0
                  : (data!.getExtraCoveragePrices!.prices[extraCoverageKey] ??
                      0) / 12,
            }))
            .sort((a, b) =>
              compareExtraCoveragesOnPage(
                {
                  key: a.extraCoverageKey,
                  isCustomizable: a.extraCoverage.valueIsCustomizable,
                },
                {
                  key: b.extraCoverageKey,
                  isCustomizable: b.extraCoverage.valueIsCustomizable,
                }
              )
            )
        : [],
    [defaultValues, data]
  );

  const handleSaveExtraCoverages = useCallback(
    async (formValues: ExtraCoveragesForm) => {
      setIsFormSubmitting(true);
      onHandleExtraCoveragesSubmitStarted();

      try {
        const nonUndefinedFormValues = omitBy(formValues, isNil);

        if ("scheduledPersonalProperty" in nonUndefinedFormValues) {
          nonUndefinedFormValues.scheduledPersonalProperty =
            nonUndefinedFormValues.scheduledPersonalProperty.map(
              (item: PersonalProperty & { id: string; price?: number }) =>
                omit(item, ["id", "price"])
            );
        }

        if ("mineSubsidenceScheduled" in nonUndefinedFormValues) {
          nonUndefinedFormValues.mineSubsidenceScheduled =
            nonUndefinedFormValues.mineSubsidenceScheduled.map(
              (item: OtherStructure & { id: string; price?: number }) =>
                omit(item, ["id", "price"])
            );
        }

        // Unfortunately TypeScript is not clever enough to pick it up.
        const policyLevelExtraCoverages = pick(
          nonUndefinedFormValues,
          policyLevelExtraCoverageKeys
        ) as SavePolicyLevelExtraCoveragesInput;
        const coverages = omit(
          nonUndefinedFormValues,
          policyLevelExtraCoverageKeys
        ) as UnitLevelExtraCoveragesInput;

        // TODO: Change after multi-unit is added
        const unitLevelExtraCoverages: SaveUnitLevelExtraCoveragesInput[] = [
          {
            exposureLocator: policy.units[0].exposureLocator,
            coverages,
          },
        ];

        await handleExtraCoveragesSubmit({
          policyLevelExtraCoverages,
          unitLevelExtraCoverages,
        });
      } catch (e) {
        console.error(e);
        onHandleExtraCoveragesSubmitError(formValues);
      }
      setIsFormSubmitting(false);
    },
    [policy, handleExtraCoveragesSubmit, onHandleExtraCoveragesSubmitError]
  );

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(handleSaveExtraCoverages)}>
        <ExtraCoveragesView
          extraCoveragesToBeRendered={extraCoveragesToBeRendered}
          isFormSubmitting={isFormSubmitting}
          isD2C={isD2C}
        />
      </Form>
    </FormProvider>
  );
}
